import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
} from "@ionic/react";
import DataHandling from "../../ClassThree/Mathematics/DataHandling";
import Patterns from "../../ClassThree/Mathematics/Patterns";
import Time from "../../ClassThree/Mathematics/Time";

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    options?: string[];
    answer: string;
}

// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion


const getRandomInt = (min: number, max: number): number =>
    Math.floor(Math.random() * (max - min + 1)) + min;

const IMO: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedType, setSelectedType] = useState(0);

    const loadRandomSyllabusQuestions = async () => {

        try {
            const data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                let response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeMathematics/Numbers.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeMathematics/Addition.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeMathematics/Subtraction.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeMathematics/Multiplication.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeMathematics/Division.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeMathematics/GeometryMaps.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeMathematics/Measurements.json");
                data.push(...await response.json());
                response = await fetch(process.env.REACT_APP_API_URL + "ClassThreeMathematics/Money.json");
                data.push(...await response.json());
            } else {
                let module = await import(`../../../datas/ClassThreeMathematics/Numbers.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeMathematics/Addition.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeMathematics/Subtraction.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeMathematics/Multiplication.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeMathematics/Division.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeMathematics/GeometryMaps.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeMathematics/Measurements.json`);
                data.push(...module.default as Question[]);
                module = await import(`../../../datas/ClassThreeMathematics/Money.json`);
                data.push(...module.default as Question[]);
            }
            setQuestions(shuffleArray(data));
            setCurrentQuestion(data[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;

        let correctAnswer: string | boolean = "";
        if (
            questions[currentIndex].type === "multiple-choice" ||
            questions[currentIndex].type === "fill-in-the-blank"
        ) {
            correctAnswer = questions[currentIndex].answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (questions[currentIndex].type === "true-false") {
            correctAnswer = questions[currentIndex].answer ?? "true";
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    console.log('here');
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    handlePatternComplete()
                }
                else {
                    console.log('here 2');
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();
        document.title =
            "Mamas Guidance - Olympiad - Class Three - International Mathematics Olympiad (IMO)";
    }, []);

    useEffect(() => {
        const patternType = getRandomInt(0, 3);
        setSelectedType(patternType);
    }, []);

    const handlePatternComplete = () => {
        const nextType = getRandomInt(0, 3); console.log(nextType)
        setSelectedType(nextType);
    };
    

    return (
        <>
            <Helmet>
                <meta name="description" content="Prepare for Class 3 IMO Olympiad with Mama's Guidance. Access syllabus details, sample questions, tips, and practice worksheets to boost your child's performance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    {selectedType === 0 ? (
                        <>
                            {questions[currentIndex] ? (
                                <>
                                    <h3 className="mb-3">{questions[currentIndex].question}</h3>
                                    {(questions[currentIndex].type === "multiple-choice" ||
                                        questions[currentIndex].type === "fill-in-the-blank") && (
                                            <IonRadioGroup
                                                onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                            >
                                                <IonList>
                                                    {(questions[currentIndex].options || []).map((option, index) => (
                                                        <IonItem
                                                            className={`grid-item ${selectedOption === option
                                                                ? option.toLowerCase() === questions[currentIndex].answer.toLowerCase()
                                                                    ? "correct"
                                                                    : "errorItems"
                                                                : ""
                                                                }`}
                                                            key={index}
                                                        >
                                                            <IonRadio slot="start" value={option} />
                                                            <IonLabel>{option}</IonLabel>
                                                        </IonItem>
                                                    ))}
                                                </IonList>
                                            </IonRadioGroup>
                                        )}
                                    {questions[currentIndex].type === "true-false" && (
                                        <IonRadioGroup
                                            onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                        >
                                            <IonList>
                                                {["true", "false"].map((option, index) => (
                                                    <IonItem key={index} className={`${selectedOption === option
                                                        ? option.toLowerCase() === questions[currentIndex].answer
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}>
                                                        <IonRadio slot="start" value={option} />
                                                        <IonLabel>{option.toUpperCase()}</IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </IonRadioGroup>
                                    )}
                                </>
                            ) : (
                                <p>Loading question...</p>
                            )}
                        </>) : selectedType === 1 ? (
                            <>
                                <DataHandling onComplete={handlePatternComplete}/>
                            </>
                        ) : selectedType === 2 ? (
                            <>
                                <Time onComplete={handlePatternComplete}/>
                            </>
                        ) : (
                        <>
                            <Patterns onComplete={handlePatternComplete}  />
                        </>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default IMO;
